import { useQuery } from "@tanstack/react-query";
import { Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";

const STYLES = [
  { id: "notes", label: "Notas" },
  { id: "runbook", label: "Runbook" },
  { id: "guide", label: "Guía" },
  { id: "checklist", label: "Checklist" },
  { id: "architecture", label: "Arquitectura" },
  { id: "onboarding", label: "Onboarding" },
  { id: "executive_brief", label: "Brief ejecutivo" },
  { id: "troubleshooting", label: "Troubleshooting" },
];

const DESTINATIONS = [
  { id: "artifact", label: "Generar artefacto (descargable)" },
  { id: "notebook_artifact", label: "Guardar en notebook" },
  { id: "draft", label: "Crear borrador editable" },
  { id: "pr", label: "Abrir PR en GitHub (Fase 7)" },
];

export function ComposePage() {
  const [title, setTitle] = useState("");
  const [style, setStyle] = useState("guide");
  const [destination, setDestination] = useState("artifact");
  const [notebookId, setNotebookId] = useState<string>("");

  const notebooks = useQuery({ queryKey: ["notebooks"], queryFn: () => api.listNotebooks() });

  const estimate = useMemo(() => {
    const base = { tokens: 1500, cost: 0.04 };
    const mult: Record<string, number> = {
      notes: 1,
      runbook: 1.5,
      guide: 1.4,
      checklist: 0.7,
      architecture: 1.6,
      onboarding: 1.8,
      executive_brief: 1.2,
      troubleshooting: 1.5,
    };
    const m = mult[style] ?? 1;
    return { tokens: Math.round(base.tokens * m), cost: base.cost * m };
  }, [style]);

  function submit() {
    if (!title.trim() || !notebookId) {
      toast.error("Elige un notebook y un título");
      return;
    }
    api.composeNotebook(notebookId, { title, style, destination, lang: "es" }).then(
      () => toast.success(`Composición encolada (${style})`),
      (err) => toast.error(`Error: ${err}`),
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Sparkles size={20} /> Componer documento
        </h1>
        <p className="text-sm text-(--text-secondary)">
          Genera Markdown, runbooks, onboarding o briefings combinando fuentes de un notebook.
        </p>
      </header>

      <section className="space-y-4 rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4">
        <Field label="Notebook origen">
          <select
            value={notebookId}
            onChange={(e) => setNotebookId(e.target.value)}
            className="w-full rounded-md border border-(--border-subtle) bg-(--bg-base) px-2 py-1.5 text-sm"
          >
            <option value="">Selecciona un notebook…</option>
            {notebooks.data?.items.map((nb) => (
              <option key={nb.id} value={nb.id}>
                {nb.title} ({nb.scope})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Título del documento">
          <Input
            value={title}
            placeholder="NetBox onboarding — guía consolidada"
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <Field label="Estilo">
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            {STYLES.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setStyle(s.id)}
                className={`rounded-md border px-2 py-1.5 text-xs ${
                  style === s.id
                    ? "border-(--accent-500) bg-(--accent-50) text-(--accent-700)"
                    : "border-(--border-subtle) hover:bg-(--bg-hover)"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Destino">
          <div className="space-y-1">
            {DESTINATIONS.map((d) => (
              <label
                key={d.id}
                className="flex items-center gap-2 rounded px-2 py-1 hover:bg-(--bg-hover)"
              >
                <input
                  type="radio"
                  name="destination"
                  checked={destination === d.id}
                  onChange={() => setDestination(d.id)}
                />
                <span className="text-sm">{d.label}</span>
              </label>
            ))}
          </div>
        </Field>

        <div className="flex items-center justify-between rounded-md bg-(--bg-base) px-3 py-2 text-xs">
          <span className="text-(--text-tertiary)">
            Coste estimado: <strong>${estimate.cost.toFixed(2)}</strong> · ~{estimate.tokens} tokens
          </span>
          <Badge variant="muted">~30s</Badge>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setTitle("")}>
            <X size={14} /> Limpiar
          </Button>
          <Button variant="primary" onClick={submit} disabled={!title.trim() || !notebookId}>
            <Sparkles size={13} /> Componer
          </Button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-(--text-tertiary)">{label}</span>
      {children}
    </div>
  );
}
